import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useNavigate } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { ApplicationForm } from "./components/application-form"
import { ApplicationsQuery, DeleteApplicationMutation, BulkDeleteApplicationsMutation } from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"

type AppItem = ResultOf<typeof ApplicationsQuery>["applications"]["nodes"][number]

const LIMIT = 25

export function ApplicationsPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: ApplicationsQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })

  const [, deleteApp] = useMutation(DeleteApplicationMutation)
  const [, bulkDeleteApps] = useMutation(BulkDeleteApplicationsMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const columns: Column<AppItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "UID",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <code className="text-xs font-mono text-muted-foreground">{row.uid}</code>
          <CopyButton text={row.uid} />
        </div>
      ),
    },
    {
      header: "Endpoints",
      cell: (row) => {
        const active = row.endpoints.filter((e) => e.status === "ACTIVE").length
        const total = row.endpoints.length
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs tabular-nums">{total}</span>
            {total > 0 && (
              <StatusBadge status={active === total ? "ACTIVE" : active > 0 ? "PENDING" : "INACTIVE"} />
            )}
          </div>
        )
      },
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteApp({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Application deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteApps({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteApplications ?? 0} applications`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Applications" description="Outbound webhook applications">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Application
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search applications..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.applications.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.applications.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No applications"
        emptyDescription="Create an application to start sending outbound webhooks"
        onRowClick={(row) => navigate(`/applications/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <ApplicationForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Application"
        description="This will permanently delete this application and all its endpoints."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Applications"
        description={`This will permanently delete ${selectedKeys.size} selected applications and all their endpoints.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
